import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Send, Square, RefreshCw, MoreVertical, Brain, Languages,
  Trash2, Archive, MessageSquarePlus, AlertTriangle, Flag, Check, X, Pencil, ChevronsUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { FictionalBadge, StagePill } from "@/components/FictionalBadge";
import { LANGUAGES } from "@/data/languages";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const draftKey = (id) => `talkmos_draft_${id}`;

export default function ChatRoom() {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [character, setCharacter] = useState(null);
  const [rel, setRel] = useState({ stage: "New", language: "English", archived: false });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [retention, setRetention] = useState(24);
  const [chatList, setChatList] = useState([]);
  const [langDialog, setLangDialog] = useState(false);
  const [dialog, setDialog] = useState(null); // 'new' | 'delete' | 'reset'
  const [delMemories, setDelMemories] = useState(false);
  const [loadedEmpty, setLoadedEmpty] = useState(false);
  const [aiDailyLimit, setAiDailyLimit] = useState(null);

  const controllerRef = useRef(null);
  const bottomRef = useRef(null);
  const lastUserRef = useRef("");

  const scrollDown = () => bottomRef.current?.scrollIntoView({ behavior: "smooth" });

  const load = useCallback(async () => {
    const res = await api.get(`/chats/${characterId}`);
    setCharacter(res.data.character);
    setRel(res.data.relationship);
    setMessages(res.data.messages);
    setRetention(res.data.retention_hours);
    setLoadedEmpty(res.data.messages.length === 0);
    setInput(localStorage.getItem(draftKey(characterId)) || "");
  }, [characterId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/chats").then((r) => setChatList(r.data)).catch(() => {}); }, [characterId]);
  useEffect(() => { scrollDown(); }, [messages, streaming]);
  useEffect(() => { localStorage.setItem(draftKey(characterId), input); }, [input, characterId]);

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    lastUserRef.current = text;
    setSuggestion(null);
    setLoadedEmpty(false);
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: text }]);
    setInput("");
    localStorage.removeItem(draftKey(characterId));
    const aiId = `a-${Date.now()}`;
    setMessages((m) => [...m, { id: aiId, role: "assistant", content: "" }]);
    setStreaming(true);

    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const resp = await fetch(`${API}/chats/${characterId}/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
        signal: controller.signal,
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = JSON.parse(line.slice(5).trim());
          if (data.type === "delta") {
            setMessages((m) => m.map((msg) => msg.id === aiId ? { ...msg, content: msg.content + data.content } : msg));
          } else if (data.type === "limit") {
            setMessages((m) => m.filter((msg) => msg.id !== aiId));
            if (data.reason === "early_ai_daily") { setAiDailyLimit(true); toast.error("You've used today's 20 AI messages. Your allowance resets at midnight IST."); }
            else if (data.reason === "trial_ai") toast.error("You've used your 10 free AI replies. Become a member to keep talking.");
            else if (data.reason === "ai_daily") toast.error("You've reached today's AI reply limit. It resets at " + (data.reset_at ? new Date(data.reset_at).toLocaleTimeString() : "the daily reset") + ".");
            else if (data.reason === "ai_period") toast.error("You've reached your membership-period AI limit. It renews with your membership.");
            else if (data.reason === "expired") toast.error("Your membership has expired. Renew to send new messages.");
            else toast.error("You've reached your AI reply limit.");
            if (data.reason === "trial_ai" || data.reason === "expired") setTimeout(() => navigate("/app/membership"), 1200);
          } else if (data.type === "ai_unavailable") {
            setMessages((m) => m.filter((msg) => msg.id !== aiId));
            toast.error("AI chat is temporarily unavailable. Your conversations and memories are safe.");
          } else if (data.type === "done") {
            setMessages((m) => m.map((msg) => msg.id === aiId ? { ...msg, id: data.message_id } : msg));
            if (data.stage) setRel((r) => ({ ...r, stage: data.stage }));
            if (data.suggestion) setSuggestion(data.suggestion);
            if (data.crisis) toast("Please reach out to real support — see the crisis numbers above.", { icon: "💛" });
          }
        }
      }
      refresh();
    } catch (e) {
      if (e.name !== "AbortError") {
        setMessages((m) => m.map((msg) => msg.id === aiId && !msg.content ? { ...msg, content: "Something went wrong. Try again." } : msg));
      }
    } finally {
      setStreaming(false);
      controllerRef.current = null;
    }
  };

  const stop = () => { controllerRef.current?.abort(); setStreaming(false); };

  const regenerate = () => {
    if (!lastUserRef.current || streaming) return;
    setMessages((m) => {
      const copy = [...m];
      if (copy[copy.length - 1]?.role === "assistant") copy.pop();
      return copy;
    });
    send(lastUserRef.current);
  };

  const approveMemory = async (content) => {
    try {
      await api.post("/memories", { character_id: characterId, content, category: suggestion.category });
      toast.success(`${character.name} will remember this`);
    } catch (e) {
      toast.error(e?.response?.data?.detail === "Memory limit reached" ? "Memory limit reached — manage them in Memories." : "Could not save memory");
    }
    setSuggestion(null);
  };

  const changeLanguage = async (lang, makeDefault) => {
    await api.patch(`/chats/${characterId}/language`, { language: lang, make_default: makeDefault });
    setRel((r) => ({ ...r, language: lang }));
    setLangDialog(false);
    toast.success(`Now chatting in ${lang}`);
  };

  const confirmNew = async () => { await api.post(`/chats/${characterId}/clear`); setMessages([]); setLoadedEmpty(true); setDialog(null); toast.success("Started a new conversation"); };
  const confirmDelete = async () => { await api.post(`/chats/${characterId}/delete`, { delete_memories: delMemories }); setDialog(null); toast.success("Conversation deleted"); navigate("/app/chats"); };
  const confirmReset = async () => { await api.post(`/chats/${characterId}/reset`); setDialog(null); toast.success("Connection reset"); navigate("/app/chats"); };
  const archive = async () => { await api.post(`/chats/${characterId}/archive`); toast.success("Chat archived"); navigate("/app/chats"); };
  const report = async (id) => { await api.post("/report", { character_id: characterId, message_id: id }); toast.success("Response reported. Thank you."); };

  if (!character) {
    return <div className="flex h-screen items-center justify-center text-[#8C8578]">Loading…</div>;
  }

  return (
    <div className="flex h-screen flex-col md:h-screen" data-testid="streaming-chat-view">
      {/* Header */}
      <header className="flex items-center gap-2 border-b border-[#2B241B] bg-[#12100E]/95 px-3 py-2.5 backdrop-blur">
        <button data-testid="chat-back" onClick={() => navigate("/app/chats")} className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm text-[#C8C2B5] hover:bg-[#1A1714]">
          <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Chats</span>
        </button>
        {character.avatar_url ? (
          <img src={character.avatar_url} alt={character.name} className="h-9 w-9 rounded-lg object-cover" />
        ) : (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#6B5720] bg-[#211B12] font-display text-sm text-[#D4AF37]" aria-label={character.name}>
            {character.name.split(" ").map((part) => part[0]).slice(0, 2).join("")}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-lg text-[#F4F1EA]">{character.name}</span>
            <FictionalBadge />
          </div>
          <div className="flex items-center gap-2">
            <StagePill stage={rel.stage} />
            <button onClick={() => setLangDialog(true)} data-testid="chat-language-btn" className="flex items-center gap-1 text-[10px] text-[#8C8578] hover:text-[#D4AF37]">
              <Languages className="h-3 w-3" /> {rel.language}
            </button>
          </div>
        </div>

        {/* Character switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="character-switcher" className="rounded-lg p-2 text-[#8C8578] hover:bg-[#1A1714]"><ChevronsUpDown className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="border-[#3D3428] bg-[#12100E] text-[#C8C2B5]">
            <DropdownMenuLabel className="text-[#8C8578]">Switch companion</DropdownMenuLabel>
            {chatList.filter((c) => c.character.id !== characterId && !c.archived).map((c) => (
              <DropdownMenuItem key={c.character.id} onClick={() => navigate(`/app/chat/${c.character.id}`)} className="gap-2">
                <img src={c.character.avatar_url} alt="" className="h-6 w-6 rounded-full object-cover" /> {c.character.name}
                {c.unread && <span className="ml-auto h-2 w-2 rounded-full bg-[#D4AF37]" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-[#2B241B]" />
            <DropdownMenuItem onClick={() => navigate("/app/discover")} className="gap-2">Discover more…</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Overflow */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="chat-overflow" className="rounded-lg p-2 text-[#8C8578] hover:bg-[#1A1714]"><MoreVertical className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60 border-[#3D3428] bg-[#12100E] text-[#C8C2B5]">
            <DropdownMenuItem data-testid="menu-leave" onClick={() => navigate("/app/chats")} className="gap-2"><ArrowLeft className="h-4 w-4" /> Leave chat</DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-memories" onClick={() => navigate("/app/memories")} className="gap-2"><Brain className="h-4 w-4" /> Memories</DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-new" onClick={() => setDialog("new")} className="gap-2"><MessageSquarePlus className="h-4 w-4" /> Start new conversation</DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-archive" onClick={archive} className="gap-2"><Archive className="h-4 w-4" /> Archive chat</DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#2B241B]" />
            <DropdownMenuItem data-testid="menu-delete" onClick={() => { setDelMemories(false); setDialog("delete"); }} className="gap-2 text-[#c98a8a]"><Trash2 className="h-4 w-4" /> Delete conversation</DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-reset" onClick={() => setDialog("reset")} className="gap-2 text-[#c98a8a]"><AlertTriangle className="h-4 w-4" /> Reset my connection</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-4 md:px-8">
        <div className="mx-auto max-w-2xl">
          <div className="mb-6 rounded-xl border border-[#2B241B] bg-[#12100E] p-3 text-center text-xs leading-relaxed text-[#8C8578]" data-testid="fictional-disclosure">
            {character.name} is a fictional, AI-powered character — not a real person, therapist or emergency service.
            Messages are automatically deleted after {retention} hours. Only memories you approve are kept.
          </div>

          {messages.map((m) => (
            <div key={m.id} className={`mb-3 flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`message-${m.role}`}>
              <div className={`group max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed animate-fade-up ${
                m.role === "user" ? "bg-[#2a2114] text-[#F4F1EA]" : "border border-[#2B241B] bg-[#1A1714] text-[#E9E4D8]"
              }`}>
                {m.content || (streaming && m.role === "assistant" ? (
                  <span className="flex gap-1 py-1">
                    <span className="dot h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                    <span className="dot h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                    <span className="dot h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                  </span>
                ) : "")}
                {m.role === "assistant" && m.content && !m.id.startsWith("a-") && (
                  <button onClick={() => report(m.id)} data-testid={`report-${m.id}`} className="ml-2 hidden text-[10px] text-[#8C8578] hover:text-[#c98a8a] group-hover:inline">
                    <Flag className="inline h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Memory approval widget */}
          {suggestion && (
            <MemoryApproval
              suggestion={suggestion}
              characterName={character.name}
              onApprove={approveMemory}
              onDismiss={() => setSuggestion(null)}
            />
          )}

          {/* Suggested chips only before first message */}
          {loadedEmpty && !streaming && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="starter-chips">
              {[character.example_opening ? "Say hi 👋" : "Hi", "How's your day going?", "Tell me something about you"].map((c) => (
                <button key={c} onClick={() => send(c.replace(" 👋", ""))} className="rounded-full border border-[#3D3428] px-3 py-1.5 text-xs text-[#C8C2B5] hover:border-[#D4AF37] hover:text-[#D4AF37]">
                  {c}
                </button>
              ))}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input */}
      <div className="border-t border-[#2B241B] bg-[#12100E] px-3 py-3 safe-bottom md:px-8">
        {aiDailyLimit && (
          <div className="mx-auto mb-3 max-w-2xl rounded-xl border border-[#5c4a1e] bg-[#1a1509] px-4 py-3" data-testid="ai-daily-limit-banner">
            <p className="text-sm text-[#F4F1EA]">You've used today's 20 AI messages. Your allowance resets at midnight IST.</p>
            <button onClick={() => navigate("/app/membership")} className="mt-2 text-sm font-semibold text-[#D4AF37] hover:text-[#E5C158]" data-testid="ai-daily-limit-waitlist">Join the private waitlist →</button>
            <p className="mt-1 text-xs text-[#8C8578]">Joining the waitlist won't increase today's limit.</p>
          </div>
        )}
        <div className="mx-auto flex max-w-2xl items-end gap-2">
          {streaming ? (
            <Button data-testid="stop-generation" onClick={stop} variant="outline" className="rounded-full border-[#3D3428] bg-transparent text-[#C8C2B5]">
              <Square className="mr-1 h-4 w-4" /> Stop
            </Button>
          ) : (
            <button data-testid="regenerate" onClick={regenerate} disabled={!lastUserRef.current} className="rounded-xl p-2.5 text-[#8C8578] hover:bg-[#1A1714] disabled:opacity-30" title="Regenerate">
              <RefreshCw className="h-5 w-5" />
            </button>
          )}
          <Textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
            placeholder={`Message ${character.name}…`}
            rows={1}
            className="max-h-32 min-h-[44px] flex-1 resize-none rounded-xl border-[#3D3428] bg-[#0A0908] text-[#F4F1EA] placeholder:text-[#8C8578]"
          />
          <Button data-testid="send-message" onClick={() => send(input)} disabled={!input.trim() || streaming} className="rounded-xl bg-[#D4AF37] px-4 py-3 text-[#0A0908] hover:bg-[#E5C158] disabled:opacity-40">
            <Send className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Language dialog */}
      <Dialog open={langDialog} onOpenChange={setLangDialog}>
        <DialogContent className="border-[#3D3428] bg-[#12100E] text-[#F4F1EA]">
          <DialogHeader><DialogTitle className="font-display text-2xl">Conversation language</DialogTitle></DialogHeader>
          <p className="text-sm text-[#C8C2B5]">Change the language for {character.name}. Other companions are unaffected.</p>
          <select
            data-testid="language-picker"
            defaultValue={rel.language}
            onChange={(e) => (window.__lang = e.target.value)}
            className="w-full rounded-xl border border-[#3D3428] bg-[#0A0908] px-4 py-3 text-[#F4F1EA]"
          >
            {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <div className="flex flex-col gap-2">
            <Button data-testid="lang-this-only" onClick={() => changeLanguage(window.__lang || rel.language, false)} className="rounded-full bg-[#D4AF37] font-semibold text-[#0A0908] hover:bg-[#E5C158]">
              Change for {character.name} only
            </Button>
            <Button data-testid="lang-default" onClick={() => changeLanguage(window.__lang || rel.language, true)} variant="outline" className="rounded-full border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714]">
              Make it my default for future chats
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New conversation */}
      <AlertDialog open={dialog === "new"} onOpenChange={(o) => !o && setDialog(null)}>
        <AlertDialogContent className="border-[#3D3428] bg-[#12100E] text-[#F4F1EA]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Start a new conversation with {character.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#C8C2B5]">The current temporary messages will be cleared, but approved memories will remain.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-new" onClick={confirmNew} className="bg-[#D4AF37] font-semibold text-[#0A0908] hover:bg-[#E5C158]">Start new conversation</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete */}
      <AlertDialog open={dialog === "delete"} onOpenChange={(o) => !o && setDialog(null)}>
        <AlertDialogContent className="border-[#3D3428] bg-[#12100E] text-[#F4F1EA]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete your conversation with {character.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#C8C2B5]">This will delete the conversation history. Approved memories will not be deleted unless you choose that option below.</AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-center gap-2 text-sm text-[#C8C2B5]">
            <Checkbox data-testid="delete-memories-check" checked={delMemories} onCheckedChange={(v) => setDelMemories(!!v)} />
            Also delete everything {character.name} remembers about me.
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={confirmDelete} className="bg-[#8B2626] text-[#F4F1EA] hover:bg-[#a32d2d]">Delete conversation</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset */}
      <AlertDialog open={dialog === "reset"} onOpenChange={(o) => !o && setDialog(null)}>
        <AlertDialogContent className="border-[#3D3428] bg-[#12100E] text-[#F4F1EA]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Reset my connection with {character.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#C8C2B5]">
              This permanently deletes your temporary messages, all approved memories for {character.name}, the relationship stage and familiarity. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#3D3428] bg-transparent text-[#C8C2B5] hover:bg-[#1A1714]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-reset" onClick={confirmReset} className="bg-[#8B2626] text-[#F4F1EA] hover:bg-[#a32d2d]">Reset connection</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MemoryApproval({ suggestion, characterName, onApprove, onDismiss }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(suggestion.content);
  return (
    <div className="mb-3 rounded-2xl border border-[#D4AF37]/40 bg-[#1A1714] p-4 animate-fade-up" data-testid="memory-approval">
      <p className="mb-2 text-sm text-[#F4F1EA]">Should {characterName} remember this?</p>
      {editing ? (
        <Textarea data-testid="memory-edit-input" value={val} onChange={(e) => setVal(e.target.value)} className="mb-3 border-[#3D3428] bg-[#0A0908] text-[#F4F1EA]" />
      ) : (
        <p className="mb-3 rounded-lg bg-[#0A0908] px-3 py-2 text-sm text-[#D4AF37]">“{val}”</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button data-testid="memory-remember" onClick={() => onApprove(val)} className="rounded-full bg-[#D4AF37] px-4 text-sm font-semibold text-[#0A0908] hover:bg-[#E5C158]">
          <Check className="mr-1 h-4 w-4" /> Remember this
        </Button>
        <Button data-testid="memory-edit" onClick={() => setEditing(!editing)} variant="outline" className="rounded-full border-[#3D3428] bg-transparent text-sm text-[#C8C2B5] hover:bg-[#24201C]">
          <Pencil className="mr-1 h-4 w-4" /> Edit before saving
        </Button>
        <Button data-testid="memory-notnow" onClick={onDismiss} variant="outline" className="rounded-full border-[#3D3428] bg-transparent text-sm text-[#8C8578] hover:bg-[#24201C]">
          <X className="mr-1 h-4 w-4" /> Not now
        </Button>
      </div>
    </div>
  );
}
